import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-bold uppercase tracking-wide transition-all hover:-translate-y-px active:translate-x-[2px] active:translate-y-[2px] active:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-charcoal-800 text-white shadow-md hover:bg-charcoal-700 border-[length:var(--border-brutal)] border-charcoal-950 dark:border-charcoal-600',
        destructive:
          'bg-error text-white shadow-md hover:bg-error-dark border-[length:var(--border-brutal)] border-charcoal-950 dark:border-charcoal-600',
        outline:
          'border-[length:var(--border-brutal)] border-charcoal-950 bg-white shadow-md hover:bg-neutral-50 text-charcoal-900 dark:bg-charcoal-800 dark:text-foreground dark:border-charcoal-600 dark:hover:bg-charcoal-700',
        secondary:
          'bg-secondary-500 text-charcoal-950 shadow-md hover:bg-secondary-400 border-[length:var(--border-brutal)] border-secondary-600',
        ghost:
          'hover:bg-neutral-100 text-charcoal-900 dark:text-foreground dark:hover:bg-charcoal-800',
        link: 'text-secondary-600 underline-offset-4 hover:underline dark:text-secondary-400',
        cta: 'bg-gradient-to-b from-secondary-400 to-secondary-500 text-charcoal-950 font-black shadow-[3px_3px_0_#4D7C0F] hover:from-secondary-300 hover:to-secondary-400 hover:shadow-[5px_5px_0_#4D7C0F] hover:-translate-y-0.5 border-[length:var(--border-brutal)] border-secondary-700 uppercase tracking-wider',
      },
      size: {
        default: 'h-10 px-6 py-2',
        xs: 'h-7 rounded-md px-2.5 text-[11px]',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-md px-8 text-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, loading = false, children, disabled, ...props },
    ref,
  ) => {
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="size-4 animate-spin" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
